import { createVideoTemplate } from "~/common/videoTemplate/template"
import type { VideoTemplate } from "~/common/videoTemplate/types"
import { useState } from "react"
import {
  App,
  Button,
  Drawer,
  Empty,
  Flex,
  Image,
  Popconfirm,
  Tag,
  Typography,
} from "antd"
import type { TableProps } from "antd"
import { PlusOutlined } from "@ant-design/icons"
import { Alert } from "~/components/Alert"
import { getVideoChannelLabel } from "~/common/constants"
import { TemplateEditor } from "./TemplateEditor"
import { MoveTemplateButton } from "./MoveTemplateButton"
import { CategoryManager } from "./CategoryManager"
import {
  SortableTemplateTable,
  TemplateDragHandle,
} from "./SortableTemplateTable"
import {
  useVideoTemplates,
  type StaffTemplateRecord,
} from "./useVideoTemplates"
import styles from "./index.css"

type EditingTemplate = {
  initial: VideoTemplate
  original?: API.VideoTemplate.Content
}

export const StaffVideoTemplates = () => {
  const templates = useVideoTemplates()
  const { message } = App.useApp()
  const [editing, setEditing] = useState<EditingTemplate | null>(null)
  const [managingCategories, setManagingCategories] = useState(false)
  const orderLocked =
    templates.loading ||
    Boolean(templates.error) ||
    templates.orderDirty ||
    templates.ordering ||
    Boolean(templates.deleting)
  const saveOrder = async () => {
    if (await templates.saveOrder()) void message.success("展示顺序已保存")
  }
  const columns: TableProps<StaffTemplateRecord>["columns"] = [
    {
      key: "drag",
      width: 52,
      render: () => <TemplateDragHandle />,
    },
    {
      title: "展示位置",
      key: "position",
      width: 90,
      render: (_, record) => record.position,
    },
    {
      title: "模板",
      key: "template",
      render: (_, record) => (
        <Flex
          align="center"
          gap={12}
        >
          {record.config?.videoCover && (
            <Image
              className={styles.cover}
              src={record.config.videoCover}
              width={48}
              height={64}
              alt={record.config.title}
            />
          )}
          <Flex
            vertical
            gap={4}
          >
            <Typography.Text strong>
              {record.config?.title || "配置格式异常"}
            </Typography.Text>
            <Typography.Text type="secondary">
              {record.config?.templateId ||
                (typeof record.content?.templateId === "string"
                  ? record.content.templateId
                  : "缺少模板ID")}
            </Typography.Text>
            {record.error && (
              <Typography.Text type="danger">{record.error}</Typography.Text>
            )}
          </Flex>
        </Flex>
      ),
    },
    {
      title: "状态",
      key: "status",
      width: 120,
      render: (_, record) => (
        <Tag color={record.config?.sameStyleAvailable ? "green" : "default"}>
          {record.config?.sameStyleAvailable ? "已开放生成" : "未开放生成"}
        </Tag>
      ),
    },
    {
      title: "分类",
      key: "categories",
      render: (_, record) =>
        record.config?.categories.length ? (
          <Flex
            gap={4}
            wrap
          >
            {record.config.categories.map((category) => (
              <Tag key={category}>{category}</Tag>
            ))}
          </Flex>
        ) : (
          "—"
        ),
    },
    {
      title: "默认模型",
      key: "model",
      render: (_, record) =>
        record.config?.model ? getVideoChannelLabel(record.config.model) : "—",
    },
    {
      title: "生成参数",
      key: "options",
      render: (_, record) =>
        record.config
          ? [
              record.config.ratio,
              record.config.resolution,
              `${record.config.duration}s`,
            ]
              .filter(Boolean)
              .join(" · ")
          : "—",
    },
    {
      title: "操作",
      key: "actions",
      width: 230,
      render: (_, record) => (
        <Flex gap={4}>
          <MoveTemplateButton
            position={record.position}
            total={templates.total}
            disabled={
              templates.loading ||
              templates.ordering ||
              Boolean(templates.deleting) ||
              Boolean(templates.orderError) ||
              !record.config ||
              templates.total < 2
            }
            onMove={(position) => templates.move(record.key, position)}
          />
          <Button
            type="link"
            disabled={!record.config || orderLocked}
            onClick={() => {
              if (record.config)
                setEditing({ initial: record.config, original: record.content })
            }}
          >
            编辑
          </Button>
          <Popconfirm
            destroyOnHidden
            title="删除模板"
            description={`确定删除“${record.config?.title || record.key}”吗？`}
            okText="删除"
            cancelText="取消"
            okButtonProps={{ danger: true }}
            cancelButtonProps={{ disabled: Boolean(templates.deleting) }}
            onConfirm={async () => {
              try {
                await templates.remove(record.key)
                void message.success("模板已删除")
              } catch (cause) {
                void message.error(
                  cause instanceof Error ? cause.message : "模板删除失败",
                )
              }
            }}
          >
            <Button
              type="link"
              danger
              disabled={orderLocked}
              loading={templates.deleting === record.key}
            >
              删除
            </Button>
          </Popconfirm>
        </Flex>
      ),
    },
  ]

  return (
    <Flex
      className={styles.page}
      vertical
      gap={20}
    >
      <Flex
        align="center"
        justify="space-between"
        gap={16}
        wrap
      >
        <Flex
          vertical
          gap={4}
        >
          <Typography.Title
            className={styles.pageTitle}
            level={2}
          >
            视频模板
          </Typography.Title>
          <Typography.Text type="secondary">
            配置模板案例、提示词、参考素材和默认生成参数。
          </Typography.Text>
        </Flex>
        <Flex gap={8}>
          <Button
            disabled={orderLocked}
            onClick={() => setManagingCategories(true)}
          >
            管理分类
          </Button>
          {templates.orderDirty && (
            <>
              <Button
                disabled={templates.ordering || templates.loading}
                onClick={templates.cancelOrder}
              >
                {templates.orderError ? "重新加载" : "取消排序"}
              </Button>
              <Button
                type="primary"
                loading={templates.ordering}
                disabled={templates.loading}
                onClick={() => void saveOrder()}
              >
                保存排序
              </Button>
            </>
          )}
          <Button
            type="primary"
            icon={<PlusOutlined />}
            disabled={orderLocked}
            onClick={() => setEditing({ initial: createVideoTemplate() })}
          >
            新增模板
          </Button>
        </Flex>
      </Flex>
      <Typography.Text type="secondary">
        拖动左侧手柄调整顺序，或点击“移动”指定展示位置，支持跨页移动。点击“保存排序”后生效，新模板默认排在末尾。
      </Typography.Text>
      {templates.orderError && (
        <Alert
          level="strong"
          title={templates.orderError}
          showIcon
        />
      )}
      {templates.error && (
        <Alert
          level="strong"
          title={templates.error}
          showIcon
          action={<Button onClick={templates.refresh}>重试</Button>}
        />
      )}
      <SortableTemplateTable
        disabled={
          templates.loading ||
          templates.ordering ||
          Boolean(templates.deleting) ||
          Boolean(templates.orderError) ||
          templates.records.length < 2 ||
          templates.records.some((record) => !record.config)
        }
        onReorder={templates.reorder}
        columns={columns}
        records={templates.records}
        loading={templates.loading || templates.ordering}
        scroll={{ x: 1000 }}
        locale={{
          emptyText: (
            <Empty
              description={
                templates.error
                  ? "模板加载失败，请重试"
                  : "暂无模板，点击新增模板开始配置"
              }
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            />
          ),
        }}
        pagination={{
          disabled:
            templates.loading ||
            templates.ordering ||
            Boolean(templates.deleting),
          current: templates.page,
          pageSize: templates.pageSize,
          total: templates.total,
          showSizeChanger: true,
          pageSizeOptions: [20, 50, 100],
          onChange: templates.setPage,
          showTotal: (total) => `共 ${total} 个模板`,
        }}
      />
      {managingCategories && (
        <CategoryManager onClose={() => setManagingCategories(false)} />
      )}
      <Drawer
        title={editing?.original ? "编辑视频模板" : "新增视频模板"}
        open={Boolean(editing)}
        size={1240}
        closable={false}
        mask={{ closable: false }}
        keyboard={false}
        destroyOnHidden
        classNames={{ body: styles.drawerBody }}
      >
        {editing && (
          <TemplateEditor
            initial={editing.initial}
            existing={Boolean(editing.original)}
            onClose={() => setEditing(null)}
            onSave={(config) => templates.save(config, editing.original)}
          />
        )}
      </Drawer>
    </Flex>
  )
}
